import { Observable, OperatorFunction, pipe } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { normalizeSearch } from './search-normalizer';

type DebouncedLatestRequestOptions = {
  debounceMs?: number;
  normalize?: boolean;
  leading?: boolean;
  trailing?: boolean;
};

export function debouncedLatestRequest<TInput, TResult>(
  requestFactory: (input: TInput) => Observable<TResult>,
  options: DebouncedLatestRequestOptions = {}
): OperatorFunction<TInput, TResult> {
  const { debounceMs = 500, normalize = true, leading = false, trailing = true } = options;

  const debounceOperator = debounceWithEdges<TInput>({
    debounceMs,
    leading,
    trailing,
  });
  return pipe(
    map((value: TInput) => {
      if (normalize && typeof value === 'string') {
        return normalizeSearch(value) as unknown as TInput;
      }
      return value;
    }),
    debounceOperator,
    distinctUntilChanged(),
    switchMap((value: TInput) => requestFactory(value))
  );
}

export function debouncedSearchLatest<T>(
  requestFactory: (input: string) => Observable<T>,
  options: Omit<DebouncedLatestRequestOptions, 'normalize'> = {}
): OperatorFunction<string | null, T> {
  return debouncedLatestRequest<string | null, T>(input => requestFactory(input ?? ''), {
    ...options,
    normalize: true,
  });
}

function debounceWithEdges<T>({
  debounceMs,
  leading,
  trailing,
}: Pick<DebouncedLatestRequestOptions, 'debounceMs' | 'leading' | 'trailing'>) {
  return (source: Observable<T>) =>
    new Observable<T>(subscriber => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      let lastValue: T | undefined;
      let hasValue = false;

      const emitTrailing = () => {
        if (hasValue && trailing) {
          subscriber.next(lastValue as T);
        }
        clearTimer();
      };

      const clearTimer = () => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        hasValue = false;
      };

      const subscription = source.subscribe({
        next: value => {
          const shouldEmitLeading = leading && !timer;
          lastValue = value;
          hasValue = true;

          if (shouldEmitLeading) {
            subscriber.next(value);
          }

          if (timer) clearTimeout(timer);
          timer = setTimeout(emitTrailing, debounceMs);
        },
        error: err => subscriber.error(err),
        complete: () => {
          if (timer) {
            clearTimeout(timer);
            if (hasValue && trailing) {
              subscriber.next(lastValue as T);
            }
          }
          subscriber.complete();
        },
      });

      return () => {
        clearTimer();
        subscription.unsubscribe();
      };
    });
}

/**
 * Custom RxJS operator that debounces search input and normalizes it.
 * Combines debounceTime, distinctUntilChanged, and search normalization
 * to prevent excessive API calls and ensure consistent search behavior.
 *
 * @param debounce - Time in milliseconds to wait after the last emission before emitting (default: 500ms)
 * @returns A pipeable operator that debounces and normalizes search strings
 *
 * @example
 * ```typescript
 * searchQuery$.pipe(
 *   debouncedSearch(),
 *   switchMap(search => this.api.search(search))
 * )
 * ```
 */
export function debouncedSearch(debounce = 500): OperatorFunction<string | null, string> {
  return pipe(
    debounceTime<string | null>(debounce),
    distinctUntilChanged<string | null>(),
    map((search: string | null) => normalizeSearch(search ?? ''))
  );
}
